import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import {
  ClientOrganizationTagsEnum,
  GetOrganizationsInGeoAreaTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';

import { useHighestGlobalRole } from '@/features/authorization/provider/useHighestGlobalRole';
import { isOrganizationType } from '@/shared/utils/organization';

type TagValue = GetOrganizationsInGeoAreaTagsEnum;

const ALL_TAGS: TagValue[] = Object.values(GetOrganizationsInGeoAreaTagsEnum);

/**
 * Default tag options: the types a territorial authority can create (frontend
 * mirror of `OrganizationTag.getCreatableOrganizationTypes`, see
 * grapevine-data). Used for every caller without global admin role whose
 * reference organization does not carry the `ADMIN` tag — including unloaded
 * organizations.
 */
const DEFAULT_TAGS: TagValue[] = [
  GetOrganizationsInGeoAreaTagsEnum.TerritorialAuthority,
  GetOrganizationsInGeoAreaTagsEnum.Volunteering,
  GetOrganizationsInGeoAreaTagsEnum.EmergencyAid,
  GetOrganizationsInGeoAreaTagsEnum.Club,
  GetOrganizationsInGeoAreaTagsEnum.Education,
  GetOrganizationsInGeoAreaTagsEnum.Religion
];

/**
 * Tag options shown in the organization-area tag filter, derived from the
 * caller's privileges (no API call):
 *
 * 1. any global admin role (SuperAdmin, GlobalConfigurationAdmin,
 *    OrganizationConfigurationAdmin) → all tags
 * 2. the reference organization carries the `ADMIN` tag (regardless of the
 *    caller's org role, e.g. FUNKER) → all tags
 * 3. everyone else → the TAO-creatable subset as default
 *
 * Always returns a tag list, so the filter chip is always visible.
 */
export const useAvailableFilterTags = (
  organization: ClientOrganization | undefined
): TagValue[] => {
  const globalRole = useHighestGlobalRole();

  if (
    organization !== undefined &&
    (globalRole !== undefined ||
      isOrganizationType(ClientOrganizationTagsEnum.Admin, organization))
  ) {
    return ALL_TAGS;
  }

  return DEFAULT_TAGS;
};
